import { useState, useCallback, useMemo } from 'react'
import { sortRows, paginateRows } from '../handlers'

/**
 * Hook to manage internal table state (sorting, pagination, selection)
 * @param {Object} options - Configuration options
 * @param {Object} options.sorting - Sorting configuration
 * @param {Object} options.pagination - Pagination configuration
 * @param {Object} options.selection - Selection configuration
 * @param {Array} options.rows - Row data
 * @returns {Object} Table state and handlers
 */
export const useTableState = ({ sorting = {}, pagination = {}, selection = {}, rows = [] }) => {
    // Sorting state
    const [sortModel, setSortModel] = useState(() => {
        if (sorting.defaultField) {
            return [
                {
                    field: sorting.defaultField,
                    direction: sorting.defaultDirection || 'asc',
                },
            ]
        }
        return []
    })

    // Pagination state
    const [currentPage, setCurrentPage] = useState(0)
    const [pageSize, setPageSize] = useState(pagination.pageSize || 10)

    // Selection state
    const [selectedRows, setSelectedRows] = useState(selection.selectedRows || [])

    // Handle sort change
    const handleSortChange = useCallback(
        (field, direction) => {
            const newSortModel = direction ? [{ field, direction }] : []

            setSortModel(newSortModel)

            if (sorting.onSortChange) {
                sorting.onSortChange(newSortModel)
            }
        },
        [sorting]
    )

    // Handle AG-Grid sort changed event
    const handleAgGridSortChanged = useCallback(
        (event) => {
            const columnState = event.api.getColumnState()
            const sortedColumn = columnState.find((col) => col.sort)

            if (sortedColumn) {
                handleSortChange(sortedColumn.colId, sortedColumn.sort)
            } else {
                handleSortChange(null, null)
            }
        },
        [handleSortChange]
    )

    // Handle page change
    const handlePageChange = useCallback(
        (newPage) => {
            setCurrentPage(newPage)

            if (pagination.onPageChange) {
                pagination.onPageChange(newPage, pageSize)
            }
        },
        [pagination, pageSize]
    )

    // Handle page size change
    const handlePageSizeChange = useCallback(
        (newPageSize) => {
            setPageSize(newPageSize)
            setCurrentPage(0) // Reset to first page

            if (pagination.onPageChange) {
                pagination.onPageChange(0, newPageSize)
            }
        },
        [pagination]
    )

    // Handle selection change
    const handleSelectionChange = useCallback(
        (newSelectedRows) => {
            setSelectedRows(newSelectedRows)

            if (selection.onSelectionChange) {
                selection.onSelectionChange(newSelectedRows)
            }
        },
        [selection]
    )

    // Handle AG-Grid selection changed event
    const handleAgGridSelectionChanged = useCallback(
        (event) => {
            const selectedNodes = event.api.getSelectedNodes()
            const newSelectedRows = selectedNodes.map((node) => node.data)
            handleSelectionChange(newSelectedRows)
        },
        [handleSelectionChange]
    )

    // Process rows (sort + paginate) for client-side mode
    const processedRows = useMemo(() => {
        let result = rows

        // Apply sorting
        if (sortModel.length > 0) {
            result = sortRows(result, sortModel)
        }

        // Apply pagination
        if (pagination.enabled) {
            result = paginateRows(result, currentPage, pageSize)
        }

        return result
    }, [rows, sortModel, currentPage, pageSize, pagination.enabled])

    // Total pages calculation
    const totalPages = useMemo(() => {
        if (!pagination.enabled || pageSize <= 0) return 1
        return Math.ceil(rows.length / pageSize)
    }, [rows.length, pageSize, pagination.enabled])

    // Total rows count
    const totalRows = rows.length

    return {
        // Sorting
        sortModel,
        handleSortChange,
        handleAgGridSortChanged,

        // Pagination
        currentPage,
        pageSize,
        totalPages,
        totalRows,
        handlePageChange,
        handlePageSizeChange,

        // Selection
        selectedRows,
        handleSelectionChange,
        handleAgGridSelectionChanged,

        // Processed data
        processedRows,
    }
}
